import API_CONFIG from '../config/api.config';
import apiClient from './apiClient';
import { getReferralCode, getReferralUrl, getMentorShareUrl } from '../utils/referral';

const delay = (ms = API_CONFIG.MOCK_DELAY) => new Promise(r => setTimeout(r, ms));

const MOCK_REFERRALS = [
  { _id: 'ref_001', name: 'Arjun K.',   email: 'arj***@gmail.com', status: 'credited',  credit: 500, date: '2026-03-01' },
  { _id: 'ref_002', name: 'Sneha R.',   email: 'sne***@gmail.com', status: 'credited',  credit: 500, date: '2026-02-22' },
  { _id: 'ref_003', name: 'Vikram S.',  email: 'vik***@gmail.com', status: 'signed_up', credit: 0,   date: '2026-02-18' },
  { _id: 'ref_004', name: 'Deepika M.', email: 'dee***@gmail.com', status: 'pending',   credit: 0,   date: '2026-03-05' },
];

const referralService = {

  // ── Get referral code & links ──────────────────────────────
  async getMyCode(userId, mentorSlug = '') {
    if (API_CONFIG.MOCK) {
      await delay();
      const code = getReferralCode(userId);
      return {
        code,
        referralUrl: getReferralUrl(code),
        profileUrl:  mentorSlug ? getMentorShareUrl(mentorSlug, code) : null,
      };
    }
    return apiClient.get('/referrals/my-code');
  },

  // ── Track click (public — no auth needed) ─────────────────
  async trackClick(mentorId, source = 'direct', refCode = '') {
    if (API_CONFIG.MOCK) return { tracked: true };
    try {
      return await apiClient.post('/referrals/track-click', { mentorId, source, refCode });
    } catch { return { tracked: false }; } // silent fail
  },

  // ── Apply referral code after signup ──────────────────────
  async applyCode(code) {
    if (API_CONFIG.MOCK) {
      await delay();
      return { message: '[MOCK] Referral code applied.', code };
    }
    return apiClient.post('/referrals/apply', { code });
  },

  // ── Get my referral history ────────────────────────────────
  async getMyReferrals() {
    if (API_CONFIG.MOCK) {
      await delay();
      const totalEarned = MOCK_REFERRALS.filter(r => r.status === 'credited').reduce((s, r) => s + r.credit, 0);
      return {
        referrals: MOCK_REFERRALS,
        totalEarned,
        pending: MOCK_REFERRALS.filter(r => ['signed_up','pending'].includes(r.status)).length,
      };
    }
    return apiClient.get('/referrals/my-referrals');
  },

  // ── Get share analytics (mentor only) ─────────────────────
  async getShareStats() {
    if (API_CONFIG.MOCK) {
      await delay();
      return {
        totalClicks:    389,
        thisWeekClicks: 47,
        conversionRate: '5.9',
        bySource: { linkedin: 145, whatsapp: 98, twitter: 67, direct: 79 },
        totalReferrals: 12,
        referralEarned: 6000,
      };
    }
    return apiClient.get('/referrals/share-stats');
  },

  // ── Complete referral after first booking ─────────────────
  async completeReferral() {
    if (API_CONFIG.MOCK) {
      await delay();
      return { credited: true, amount: 500 };
    }
    return apiClient.post('/referrals/complete');
  },

  // ── Get points balance, tier, breakdown & activity ─────────
  // Points schema:
  //   referral_signup    → 100 pts per friend who registers
  //   referral_converted → 250 pts per friend who books
  //   event_booked       → 50 pts per event
  //   profile_complete   → up to 100 pts (scales with %)
  //   first_booking      → 150 pts (one-time)
  //   review_left        → 25 pts per review
  // Tiers: Silver 0–999 · Gold 1000–2499 · Platinum 2500+
  async getMyPoints() {
    if (API_CONFIG.MOCK) {
      await delay();
      return {
        totalPoints: 1250,
        tier: 'gold',
        breakdown: {
          referrals:       700,
          eventsBooked:    150,
          profileComplete: 100,
          firstBooking:    150,
          reviewsLeft:      75,
          other:            75,
        },
        recentActivity: [
          { type: 'referral_converted', desc: 'Arjun K. booked a session',   points: 250, date: '2026-03-01' },
          { type: 'referral_converted', desc: 'Sneha R. booked a session',    points: 250, date: '2026-02-22' },
          { type: 'event_booked',       desc: 'AI for Beginners workshop',    points:  50, date: '2026-02-20' },
          { type: 'referral_signup',    desc: 'Vikram S. joined DevBrains',   points: 100, date: '2026-02-18' },
          { type: 'profile_complete',   desc: 'Profile 100% complete',        points: 100, date: '2026-02-15' },
          { type: 'event_booked',       desc: 'System Design Masterclass',    points:  50, date: '2026-02-12' },
          { type: 'review_left',        desc: 'Review for Amar Krishnan',     points:  25, date: '2026-01-12' },
          { type: 'first_booking',      desc: 'First session booked',         points: 150, date: '2026-01-10' },
        ],
      };
    }
    return apiClient.get('/referrals/my-points');
  },

  // ── Award points for a specific action (called internally) ──
  async awardPoints(action, meta = {}) {
    if (API_CONFIG.MOCK) {
      await delay(200);
      const pointMap = {
        referral_signup:    100,
        referral_converted: 250,
        event_booked:        50,
        profile_complete:   100,
        first_booking:      150,
        review_left:         25,
      };
      return { awarded: pointMap[action] || 0, action };
    }
    return apiClient.post('/referrals/award-points', { action, ...meta });
  },
};

export default referralService;
